<script lang="ts">
export default {
  name: 'DashboardSubscriptions',
};
</script>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  AlertTriangleIcon,
  FileTextIcon,
  HistoryIcon,
  InfoIcon,
  LinkIcon,
  MailXIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  SendIcon,
  Trash2Icon,
} from '@lucide/vue';
import Alert from '@/components/v2/Alert.vue';
import AppButton from '@/components/v2/AppButton.vue';
import TableSkeleton from '@/components/v2/TableSkeleton.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import NotificationSettingsTabs from '@/components/NotificationSettingsTabs.vue';
import DashboardSubscriptionForm from '@/components/subscriptions/DashboardSubscriptionForm.vue';
import DashboardSubscriptionHistory from '@/components/subscriptions/DashboardSubscriptionHistory.vue';
import SubscriptionRunBadge from '@/components/subscriptions/SubscriptionRunBadge.vue';
import { useSendNow } from '@/composables/useSendNow';
import { useToast } from '@/composables/useToast';
import { dashboardSubscriptionService, isConflictError } from '@/services/DashboardSubscriptionService';
import { DashboardSubscription, DashboardSubscriptionRequest, SubscribableDashboard } from '@/types/DashboardSubscription';
import { describeSchedule, formatRunTime, timeZoneCity } from '@/utilities/SubscriptionSchedule';

type Panel = { kind: 'new' } | { kind: 'edit'; id: number } | { kind: 'history'; id: number };

const route = useRoute();
const router = useRouter();
const toast = useToast();

const loading = ref(true);
const loadError = ref<string>();
const subscriptions = ref<DashboardSubscription[]>([]);
const dashboards = ref<SubscribableDashboard[]>([]);
const panel = ref<Panel | null>(null);
const removing = ref<{ subscription: DashboardSubscription; fromEmail: boolean } | null>(null);
const removePending = ref(false);
const historyKey = ref(0);

const columns = ['Dashboard', 'Schedule', 'Format', 'Last delivery', 'Status', ''];

const activeCount = computed(() => subscriptions.value.filter((s) => s.status === 'ACTIVE').length);
const pausedCount = computed(() => subscriptions.value.length - activeCount.value);
const failedCount = computed(() => subscriptions.value.filter((s) => s.lastRun?.result === 'FAILED').length);

function isOpen(kind: Panel['kind'], id?: number): boolean {
  if (!panel.value || panel.value.kind !== kind) {
    return false;
  }
  return panel.value.kind === 'new' || panel.value.id === id;
}

function toggle(kind: 'edit' | 'history', id: number) {
  panel.value = isOpen(kind, id) ? null : { kind, id };
}

// A PDF subscription whose dashboard has left the render allow-list fails every send until it
// is switched to a link.
const pdfWithdrawn = (subscription: DashboardSubscription) => subscription.outputFormat === 'PDF' && !subscription.pdfAvailable;

async function load() {
  try {
    [subscriptions.value, dashboards.value] = await Promise.all([dashboardSubscriptionService.list(), dashboardSubscriptionService.dashboards()]);
    loadError.value = undefined;
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : 'Your subscriptions could not be loaded.';
  } finally {
    loading.value = false;
  }
}

async function create(request: DashboardSubscriptionRequest) {
  try {
    await dashboardSubscriptionService.create(request);
  } catch (error) {
    if (isConflictError(error)) {
      throw new Error('You already subscribe to this dashboard. Edit it from the list instead.');
    }
    throw error;
  }
  toast.success('Subscription created.');
  panel.value = null;
  await load();
}

async function update(id: number, request: DashboardSubscriptionRequest) {
  await dashboardSubscriptionService.update(id, request);
  toast.success('Subscription updated.');
  panel.value = null;
  await load();
}

async function toggleStatus(subscription: DashboardSubscription) {
  const resuming = subscription.status === 'PAUSED';
  try {
    await dashboardSubscriptionService.setStatus(subscription.id, resuming ? 'ACTIVE' : 'PAUSED');
    toast.success(resuming ? 'Subscription resumed.' : 'Subscription paused.');
    await load();
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'The subscription could not be changed.');
  }
}

async function confirmRemove(): Promise<boolean> {
  if (!removing.value || removePending.value) {
    return false;
  }
  removePending.value = true;
  try {
    await dashboardSubscriptionService.remove(removing.value.subscription.id);
    toast.success(removing.value.fromEmail ? "You've been unsubscribed." : 'Subscription deleted.');
    panel.value = null;
    await load();
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'The subscription could not be deleted.');
  } finally {
    removePending.value = false;
  }
  return true;
}

function closeRemove() {
  const fromEmail = removing.value?.fromEmail;
  removing.value = null;
  if (fromEmail) {
    router.replace({ query: {} });
  }
}

// The email's unsubscribe link lands here after sign-in, so the confirmation runs once the
// list has loaded. On a failed load the query is kept, so a reload can still offer it.
function openUnsubscribeFromEmail() {
  if (!route.query.unsubscribe || loading.value || loadError.value) {
    return;
  }
  const requested = Number(route.query.unsubscribe);
  const subscription = subscriptions.value.find((s) => s.id === requested);
  if (subscription) {
    removing.value = { subscription, fromEmail: true };
    return;
  }
  toast.info('This subscription no longer exists, so there is nothing to unsubscribe from.');
  router.replace({ query: {} });
}

const { send, isSending, isCoolingDown } = useSendNow(async () => {
  historyKey.value++;
  await load();
});

// Another unsubscribe link followed while this page is open reuses the component.
watch(() => route.query.unsubscribe, openUnsubscribeFromEmail);

onMounted(async () => {
  await load();
  openUnsubscribeFromEmail();
});
</script>

<template>
  <div class="flex w-full flex-col items-center pt-5">
    <h1>Manage Your Notifications</h1>
    <NotificationSettingsTabs />

    <main class="mt-5 w-full max-w-6xl px-4 pb-10 sm:px-8">
      <div class="mb-4 flex gap-2.5 rounded-lg border border-solid border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-900">
        <InfoIcon
          :size="16"
          class="mt-0.5 shrink-0" />
        <p>
          Subscriptions email you a scheduled copy of a dashboard. You only see dashboards you have access to, and these subscriptions are yours
          alone.
        </p>
      </div>

      <TableSkeleton
        v-if="loading"
        :columns="columns"
        :rows="3"
        :show-filter-bar="false" />

      <Alert
        v-else-if="loadError"
        type="error">
        {{ loadError }}
      </Alert>

      <template v-else>
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div
            class="flex gap-2 text-sm text-gray-500"
            data-testid="subscription-counts">
            <span class="rounded-lg border border-solid border-gray-200 bg-gray-50 px-3 py-1">
              <b class="mr-1 text-emerald-700">{{ activeCount }}</b
              >Active
            </span>
            <span class="rounded-lg border border-solid border-gray-200 bg-gray-50 px-3 py-1">
              <b class="mr-1">{{ pausedCount }}</b
              >Paused
            </span>
            <span class="rounded-lg border border-solid border-gray-200 bg-gray-50 px-3 py-1">
              <b
                class="mr-1"
                :class="failedCount ? 'text-red-700' : ''"
                >{{ failedCount }}</b
              >Failed last send
            </span>
          </div>
          <AppButton
            primary
            data-testid="new-subscription"
            @click="panel = { kind: 'new' }">
            <PlusIcon :size="16" />
            New subscription
          </AppButton>
        </div>

        <section class="overflow-hidden rounded-xl border border-solid border-gray-200 bg-white drop-shadow-xs">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-solid border-gray-200 bg-gray-50 text-xs tracking-wide text-gray-500 uppercase">
                <th
                  v-for="column in columns"
                  :key="column"
                  class="px-4 py-3 text-left font-semibold">
                  {{ column }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-if="isOpen('new')">
                <td
                  colspan="6"
                  class="bg-gray-50/50 p-3">
                  <DashboardSubscriptionForm
                    :dashboards="dashboards"
                    :save="create"
                    @cancel="panel = null" />
                </td>
              </tr>

              <template
                v-for="subscription in subscriptions"
                :key="subscription.id">
                <tr
                  class="hover:bg-gray-50/50"
                  data-testid="subscription-row">
                  <td class="px-4 py-3">
                    <div class="font-medium">{{ subscription.dashboardName }}</div>
                    <div
                      v-if="subscription.accountName"
                      class="text-xs text-gray-500">
                      {{ subscription.accountName }}
                    </div>
                    <div
                      v-if="!subscription.dashboardAccessible"
                      class="mt-0.5 text-xs text-amber-700">
                      You no longer have access to this dashboard.
                    </div>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    {{ describeSchedule(subscription) }}
                    <div class="text-xs text-gray-500">{{ timeZoneCity(subscription.timeZone) }}</div>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <span
                      v-if="pdfWithdrawn(subscription)"
                      class="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs text-amber-800"
                      title="This dashboard can no longer be sent as a PDF. Edit the subscription to switch to a link."
                      data-testid="subscription-pdf-withdrawn">
                      <AlertTriangleIcon :size="12" />PDF unavailable
                    </span>
                    <span
                      v-else-if="subscription.outputFormat === 'PDF'"
                      class="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs text-emerald-700">
                      <FileTextIcon :size="12" />PDF
                    </span>
                    <span
                      v-else
                      class="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs text-amber-800">
                      <LinkIcon :size="12" />Link only
                    </span>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <template v-if="subscription.lastRun">
                      <SubscriptionRunBadge
                        v-if="subscription.lastRun.result !== 'DELIVERED'"
                        :result="subscription.lastRun.result" />
                      <div class="font-mono text-xs">{{ formatRunTime(subscription.lastRun) }}</div>
                    </template>
                    <span
                      v-else
                      class="font-mono text-xs"
                      >Not sent yet</span
                    >
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <span
                      class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs"
                      :class="
                        subscription.status === 'ACTIVE'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'border border-solid border-gray-200 bg-gray-50 text-gray-500'
                      ">
                      <component
                        :is="subscription.status === 'ACTIVE' ? PlayIcon : PauseIcon"
                        :size="12" />
                      {{ subscription.status === 'ACTIVE' ? 'Active' : 'Paused' }}
                    </span>
                  </td>
                  <td class="px-2 py-3 whitespace-nowrap">
                    <div class="flex justify-end gap-1">
                      <button
                        v-if="subscription.dashboardAccessible"
                        class="btn btn-ghost btn-xs"
                        data-testid="subscription-send-now"
                        :disabled="isSending(subscription.id) || isCoolingDown(subscription.id)"
                        :title="isCoolingDown(subscription.id) ? 'Sent just now. You can send again in a minute.' : undefined"
                        @click="send(subscription)">
                        <span
                          v-if="isSending(subscription.id)"
                          class="loading loading-xs loading-spinner"></span>
                        <SendIcon
                          v-else
                          :size="14" />Send now
                      </button>
                      <button
                        class="btn btn-ghost btn-xs"
                        data-testid="subscription-history"
                        @click="toggle('history', subscription.id)">
                        <HistoryIcon :size="14" />History
                      </button>
                      <button
                        v-if="subscription.dashboardAccessible"
                        class="btn btn-ghost btn-xs"
                        data-testid="subscription-edit"
                        @click="toggle('edit', subscription.id)">
                        <PencilIcon :size="14" />Edit
                      </button>
                      <button
                        v-if="subscription.dashboardAccessible || subscription.status === 'ACTIVE'"
                        class="btn btn-ghost btn-xs"
                        data-testid="subscription-toggle-status"
                        @click="toggleStatus(subscription)">
                        <component
                          :is="subscription.status === 'ACTIVE' ? PauseIcon : PlayIcon"
                          :size="14" />
                        {{ subscription.status === 'ACTIVE' ? 'Pause' : 'Resume' }}
                      </button>
                      <button
                        class="btn text-red-700 btn-ghost btn-xs"
                        title="Delete subscription"
                        data-testid="subscription-delete"
                        @click="removing = { subscription, fromEmail: false }">
                        <Trash2Icon :size="14" />
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="isOpen('edit', subscription.id)">
                  <td
                    colspan="6"
                    class="bg-gray-50/50 p-3">
                    <DashboardSubscriptionForm
                      :dashboards="dashboards"
                      :subscription="subscription"
                      :save="(request) => update(subscription.id, request)"
                      @cancel="panel = null" />
                  </td>
                </tr>
                <tr v-if="isOpen('history', subscription.id)">
                  <td
                    colspan="6"
                    class="bg-gray-50/50 p-3">
                    <DashboardSubscriptionHistory
                      :key="`${subscription.id}-${historyKey}`"
                      :subscription-id="subscription.id" />
                  </td>
                </tr>
              </template>
            </tbody>
          </table>

          <div
            v-if="subscriptions.length === 0 && !isOpen('new')"
            class="flex flex-col items-center gap-2 p-10 text-gray-500"
            data-testid="no-subscriptions">
            <MailXIcon
              :size="32"
              class="text-gray-400" />
            <p>You have no dashboard subscriptions yet.</p>
          </div>
        </section>
      </template>
    </main>

    <ConfirmModal
      v-if="removing"
      :title="removing.fromEmail ? 'Unsubscribe' : 'Delete subscription'"
      :subtitle="removing.subscription.dashboardName"
      :action="confirmRemove"
      :is-confirm-disabled="() => removePending"
      :confirm-text="removing.fromEmail ? 'Unsubscribe' : 'Delete'"
      size="sm"
      :show="true"
      @closed="closeRemove">
      <p class="p-2">You'll stop receiving this dashboard by email, and its delivery history will be removed. You can subscribe again at any time.</p>
    </ConfirmModal>
  </div>
</template>
